import {
  useCreateDiscoverySession,
  type CreateSearchModeMessageRequest,
  type DiscoverySession,
  type FilterConditions,
} from '@/entities/discovery';
import { SOURCE_PLATFORM_TYPE } from '@/entities/public-filter';
import { atom, useAtom, useSetAtom } from 'jotai';
import { MAX_FOLLOWER, MIN_FOLLOWER } from '../constants';
import { discoveryMessagesAtom } from './discovery-message-store';

const initialFilterConditions: FilterConditions = {
  start_date: null,
  end_date: null,
  platform: [SOURCE_PLATFORM_TYPE.TIKTOK],
  engagement: {
    followers_min: MIN_FOLLOWER,
    followers_max: MAX_FOLLOWER,
  },
};

const initialSearchReqeust: CreateSearchModeMessageRequest = {
  message: '',
  search_conditions: {
    filter_conditions: initialFilterConditions,
    post_feature_conditions: [],
  },
};

export { initialFilterConditions };

const viewStateAtom = atom<'initializing' | 'idle' | 'chat'>('initializing');
const sessionAtom = atom<DiscoverySession | null>(null);
export const searchRequestAtom = atom<CreateSearchModeMessageRequest>(initialSearchReqeust);
const tempFilterAtom = atom<FilterConditions>(initialFilterConditions);
const isSearchingAtom = atom<boolean>(false);
export const pendingAutoSearchAtom = atom<boolean>(false);

export const useDiscoveryStore = () => {
  const [viewState, setViewState] = useAtom(viewStateAtom);
  const [session, setSession] = useAtom(sessionAtom);
  const [searchRequest, setSearchRequest] = useAtom(searchRequestAtom);
  const [tempFilter, setTempFilter] = useAtom(tempFilterAtom);

  const [isSearching, setIsSearching] = useAtom(isSearchingAtom);
  const [pendingAutoSearch, setPendingAutoSearch] = useAtom(pendingAutoSearchAtom);

  const setMessages = useSetAtom(discoveryMessagesAtom);

  const createSessionMutation = useCreateDiscoverySession();

  const setPrompt = (message: string) => {
    setSearchRequest((prev) => {
      return {
        ...prev,
        message,
      };
    });
  };

  const setFilters = (filters: FilterConditions) => {
    setSearchRequest((prev) => ({
      ...prev,
      search_conditions: {
        ...prev.search_conditions,
        filter_conditions: filters,
      },
    }));
    setTempFilter(filters);
  };

  const setPostFeatures = (features: string[]) => {
    setSearchRequest((prev) => ({
      ...prev,
      search_conditions: {
        ...prev.search_conditions,
        post_feature_conditions: features,
      },
    }));
  };

  const resetSession = async () => {
    setSession(null);
    setMessages([]);
    setSearchRequest(initialSearchReqeust);
    setTempFilter(initialFilterConditions);
    setIsSearching(false);
    setPendingAutoSearch(false);
    const newSession = await createSessionMutation.mutateAsync();
    setSession(newSession);
    setViewState('idle');
    return newSession;
  };

  return {
    session,
    setSession,

    viewState,
    setViewState,
    searchRequest,
    setPrompt,
    filters: searchRequest.search_conditions.filter_conditions,
    setFilters,
    tempFilter,
    setTempFilter,
    postFeatures: searchRequest.search_conditions.post_feature_conditions,
    setPostFeatures,
    isSearching,
    setIsSearching,
    pendingAutoSearch,
    setPendingAutoSearch,
    resetSession,
  };
};
